import React from 'react'
import styled, { css } from 'styled-components'
import { useGlobalLabels } from '@sentecacommerce-theme/lib'
import { Typography } from '../'

const renderPrice = (
  price?: Price | string | number | false,
  size: Sizes = 'normal',
  currency?: string,
  isInitial?: boolean,
  isDiscounted?: boolean,
  hideRegularPrice?: boolean,
  hidePromoLabel?: boolean
) => {
  if (!price && typeof price !== 'number') return null

  if (typeof price === 'number' || typeof price === 'string') {
    const [amount, pennies] = price.toString().split('.')
    return (
      <RegularPrice
        size={size}
        isDiscounted={isDiscounted}
        hideRegularPrice={hideRegularPrice}
      >
        {price ? amount : null}
        <Pennies size={size} isInitial={isInitial}>
          .{price ? pennies?.[0] || '0' : null}
          {price ? pennies?.[1] || '0' : null}
        </Pennies>
        <CurrencyLabel size={size} isInitial={isInitial}>
          {price ? currency : null}{' '}
        </CurrencyLabel>
      </RegularPrice>
    )
  }

  return (
    <RegularPrice
      size={size}
      isDiscounted={isDiscounted}
      hideRegularPrice={hideRegularPrice}
    >
      {price.price}
      <Pennies size={size} isInitial={isInitial}>
        .{price.pennies[0] || '0'}
        {price.pennies[1] || '0'}
      </Pennies>
      <CurrencyLabel size={size} isInitial={isInitial}>
        {price.currency || currency}
      </CurrencyLabel>
    </RegularPrice>
  )
}

export const Price = ({
  price,
  discountedPrice,
  discountPercentage,
  size = 'normal',
  currency,
  hideDiscountPercentage = false,
  isProductPage = false,
  hideRegularPrice,
  hidePromoLabel = false,
}: PriceProps) => {
  const isDiscounted = Boolean(discountedPrice)
  const { signs } = useGlobalLabels()
  return (
    <Container>
      <PriceLabel>
        {!isDiscounted &&
          renderPrice(
            price,
            size,
            currency,
            true,
            false,
            hideRegularPrice,
            hidePromoLabel
          )}

        {isDiscounted && (
          <DiscountedPriceContainer isProductPage={isProductPage}>
            <DiscountedPrice>
              {renderPrice(
                discountedPrice,
                size,
                currency,
                undefined,
                undefined,
                undefined,
                hidePromoLabel
              )}
            </DiscountedPrice>
            <InitialPrice
              isDiscounted={isDiscounted}
              isProductPage={isProductPage}
            >
              {renderPrice(
                price,
                size,
                currency,
                true,
                isDiscounted,
                undefined,
                hidePromoLabel
              )}
            </InitialPrice>
            {!hideDiscountPercentage && !hidePromoLabel && (
              <DiscountPercentage size={size}>
                {`${signs?.minus}${discountPercentage}${signs?.percent}`}
              </DiscountPercentage>
            )}
          </DiscountedPriceContainer>
        )}
      </PriceLabel>
    </Container>
  )
}

type Sizes = 'tiny' | 'small' | 'normal' | 'xs' | 'md'

type Price = {
  pennies: number | string
  currency: string
  price: number
  position?: string
}

type PriceProps = {
  price: Price | string | number
  discountedPrice?: Price | string | number | false
  discountPercentage?: number | null
  size?: Sizes
  currency?: string
  hideDiscountPercentage?: boolean
  hideRegularPrice?: boolean
  isProductPage?: boolean
  hidePromoLabel?: boolean
}

type StyledProps = {
  isInitial?: boolean
  size?: Sizes
  isDiscounted?: boolean
  hideRegularPrice?: boolean
  isProductPage?: boolean
}
const DiscountedPriceContainer = styled.div.attrs({
  className: 'd-flex',
})<{ isProductPage: boolean }>`
  flex-direction: row;
  align-items: flex-end;
`

const Container = styled.div<StyledProps>`
  display: flex;
  align-items: center;
`

const PriceLabel = styled.span<StyledProps>`
  display: flex;
  align-items: flex-end;
`

const RegularPrice = styled(Typography).attrs({
  variant: 1,
  as: 'span',
})<StyledProps>`
  ${(props) =>
    props.size === 'md' &&
    css`
      font-size: 2.4rem;
    `}

  ${(props) =>
    props.size === 'tiny' &&
    css`
      font-size: 1.8rem;
    `}

  ${(props) =>
    props.size === 'xs' &&
    css`
      font-size: 1rem !important;
      @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
        font-size: 1.2rem !important;
      }
    `}

  ${(props) =>
    props.size === 'small' &&
    css`
      font-size: 2.6rem;
    `}
  color: ${({ isDiscounted, hideRegularPrice, theme }) =>
    hideRegularPrice
      ? theme.palette.danger
      : isDiscounted && theme.palette.initialPrice};
  line-height: 0.9;
  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    font-size: 2rem;
  }
`

const InitialPrice = styled.span<StyledProps>`
  text-decoration: line-through;
  font-size: 1.3rem;
  margin: 0 1rem;
  position: relative;
  top: 0.3rem;

  ${({ isProductPage }) =>
    isProductPage &&
    css`
      top: 0.2rem;
      margin: 0 0.5rem;
    `}

  color: ${({ theme }) => theme.palette.initialPrice};
  & span {
    font-size: 1.3rem;
    line-height: 1.6;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
    font-size: 1.5rem;
    & span {
      font-size: 1.5rem;
      line-height: 1.6;
    }
  }
`

const CurrencyLabel = styled.span<StyledProps>`
  font-size: 1rem;
`

const Pennies = styled.span<StyledProps>`
  ${(props) =>
    css`
      font-size: 1rem;
      vertical-align: top;
      position: relative;
      bottom: ${!props.isInitial ? '-0.9rem' : 0};

      @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
        font-size: 1.2rem;
        bottom: ${!props.isInitial ? '-0.5rem' : 0};
      }

      ${props.size === 'tiny' &&
      css`
        bottom: ${props.isInitial ? '0' : '-1.3rem'};
      `}

      ${props.size === 'md' &&
      css`
        font-size: 1.5rem !important;
      `}
    `}
`

const DiscountPercentage = styled.span.attrs({
  className: 'p-2',
})<StyledProps>`
  font-size: 1.4rem;
  line-height: 1;
  border-radius: 0.3rem;
  color: ${({ theme }) => theme.palette.secondary};
  background: ${({ theme }) => theme.palette.danger};
`

const DiscountedPrice = styled(Typography).attrs({
  variant: 1,
  as: 'span',
})<StyledProps>`
  line-height: 1;
  & span {
    color: ${({ theme }) => theme.palette.danger};
  }
`
